/**
 * UUID v7 nach RFC 9562: 48 Bit Unix-Zeit in Millisekunden, danach Zufall.
 *
 * Wichtig für die Outbox (PHASENPLAN 1.2): Die Sortierung nach
 * `idempotencyKey` ergibt die FIFO-Reihenfolge, ohne dass ein eigenes
 * Sequenzfeld nötig wäre.
 *
 * Reiner Zufall reicht dafür aber **nicht**: Innerhalb derselben Millisekunde
 * entscheidet er die Reihenfolge, und genau dort entstehen die kritischen
 * Einträge — beim Abschluss werden Protokoll, Abschluss und alle Fotos in
 * einem Zug eingereiht. Ohne Gegenmaßnahme kann ein Foto vor der Anlage des
 * Protokolls gesendet werden.
 *
 * Deshalb wird das Feld `rand_a` (12 Bit) als monotoner Zähler innerhalb einer
 * Millisekunde geführt — das ist die in RFC 9562 §6.2 beschriebene Methode.
 * Damit sind 4096 streng geordnete IDs je Millisekunde möglich; darüber hinaus
 * rückt der Zeitstempel um eine Millisekunde vor.
 */

/** Zustand des monotonen Zählers. Gilt je Laufzeitumgebung (Tab, Worker). */
let letzteZeit = -1;
let zaehler = 0;

/** 12 Bit — mehr passt nicht in `rand_a`. */
const ZAEHLER_MAX = 0xfff;

const zufallsBytes = (anzahl: number): Uint8Array => {
  const bytes = new Uint8Array(anzahl);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
};

const alsHex = (bytes: Uint8Array): string =>
  [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');

/**
 * Erzeugt eine UUID v7. `zeit` ist nur für Tests gedacht und wird dann exakt
 * übernommen; ohne Angabe schützt die Funktion zusätzlich gegen eine
 * rückwärts laufende Systemuhr.
 */
export const uuidV7 = (zeit?: number): string => {
  const festeZeit = zeit !== undefined;
  let ms = Math.floor(festeZeit ? zeit : Date.now());

  // Springt die Uhr zurück, bleibt die zuletzt verwendete Zeit stehen — sonst
  // stünde ein neuer Eintrag plötzlich vor einem älteren.
  if (!festeZeit && ms < letzteZeit) ms = letzteZeit;

  if (ms === letzteZeit) {
    zaehler += 1;
    if (zaehler > ZAEHLER_MAX) {
      zaehler = 0;
      // Bei fester Zeit lässt sich nicht vorrücken; dort bleibt es bei der
      // Eindeutigkeit über `rand_b`.
      if (!festeZeit) ms += 1;
    }
  } else {
    zaehler = 0;
  }
  letzteZeit = ms;

  const bytes = new Uint8Array(16);
  const zufall = zufallsBytes(8);

  // 48 Bit Zeitstempel (big endian)
  const zeitAlsBigInt = BigInt(ms);
  for (let i = 0; i < 6; i += 1) {
    bytes[i] = Number((zeitAlsBigInt >> BigInt(8 * (5 - i))) & 0xffn);
  }

  // Byte 6/7: Version 7 plus die 12 Zählerbits (rand_a)
  bytes[6] = 0x70 | ((zaehler >> 8) & 0x0f);
  bytes[7] = zaehler & 0xff;

  // Byte 8-15: Variante 10xx plus 62 Bit Zufall (rand_b)
  bytes.set(zufall, 8);
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = alsHex(bytes);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
};

/** Nur für Tests: setzt den monotonen Zähler zurück. */
export const uuidV7ZustandZuruecksetzen = (): void => {
  letzteZeit = -1;
  zaehler = 0;
};

/** Liest den Zeitstempel aus einer UUID v7 zurück (Millisekunden seit Epoch). */
export const uuidV7Zeitstempel = (uuid: string): number => {
  const hex = uuid.replace(/-/g, '').slice(0, 12);
  return Number.parseInt(hex, 16);
};

const UUID_MUSTER = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const istUuidV7 = (wert: string): boolean => UUID_MUSTER.test(wert);

/**
 * Kryptografisch zufälliges, URL-sicheres Token (Standard 32 Byte) für
 * Download-Links. Base64url ohne Padding.
 */
export const erzeugeToken = (bytes = 32): string => {
  const roh = zufallsBytes(bytes);
  let binaer = '';
  for (const b of roh) binaer += String.fromCharCode(b);
  const base64 = typeof btoa === 'function' ? btoa(binaer) : Buffer.from(roh).toString('base64');
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** Zufällige, stabile Geräte-ID (v4-Form, reicht als Gerätemerkmal). */
export const erzeugeGeraeteId = (): string => globalThis.crypto.randomUUID();
